import React, { useState } from 'react';
import { Table, Tag, Space, Button, Modal, Descriptions, Card, Row, Col, Divider, Transfer, Form, Input, Select, DatePicker, Typography, Radio, message, InputNumber, Cascader } from 'antd';
import { EyeOutlined, RollbackOutlined, SettingOutlined, SearchOutlined, ReloadOutlined, DownOutlined, UpOutlined, ExportOutlined } from '@ant-design/icons';
import {
  ORDER_SOURCE_OPTIONS,
  APP_DIRECTION_OPTIONS,
  ORDER_SOURCE_COLORS,
  APP_DIRECTION_COLORS,
  getOrderSourceLabels,
  getAppDirectionLabels,
  isUnlockOrder,
} from '../../../utils/valueOrderDisplay';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;

/** 逐行 tag 渲染（订单来源 / 应用方向 多字段拼接展示，每行一个 tag） */
const renderTagLines = (labels, colorMap) => {
  if (!labels || labels.length === 0) return <span style={{ color: '#ccc' }}>-</span>;
  return (
    <div>
      {labels.map((label) => (
        <div key={label} style={{ marginBottom: 2 }}>
          <Tag color={colorMap[label] || 'blue'} style={{ border: 'none', marginRight: 4 }}>{label}</Tag>
        </div>
      ))}
    </div>
  );
};

/** 币种符号 */
const getCurrencySymbol = (currency) => {
  if (currency === '美元' || currency === 'USD') return '$';
  if (currency === '人民币' || currency === 'CNY') return '¥';
  return '';
};

/** 最后退款时间（Local）：取该订单退款记录中最新一笔的时间，无退款返回 null */
const getLastRefundTime = (record) => {
  const records = record?.refundRecords || [];
  const times = records
    .map((r) => r.refundTimeLocal)
    .filter((t) => t && t !== '-')
    .sort();
  return times.length > 0 ? times[times.length - 1] : null;
};

/** 退款总额：该订单所有退款单金额求和（多笔累加），无退款返回 0 */
const getRefundTotal = (record) => {
  const records = record?.refundRecords || [];
  return records.reduce((sum, r) => sum + (parseFloat(r.refundAmount) || 0), 0);
};

const ValueAddedOrder = () => {
  const [form] = Form.useForm();
  const [expand, setExpand] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [refundVisible, setRefundVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [columnConfigVisible, setColumnConfigVisible] = useState(false);
  const [refundType, setRefundType] = useState('full');

  // 订单支付 + 退款统计（mock：本页小计 = 当前筛选结果，总计 = 全量数据）
  const paymentStats = {
    count: 1,
    pageSubtotal: { rmb: '149.00', usd: '0' },
    total: { rmb: '18010.20', usd: '0' },
  };

  const refundStats = {
    count: 3,
    pageSubtotal: { rmb: '299.00', usd: '5.00' },
    total: { rmb: '8050.50', usd: '120.00' },
  };

  // 模拟数据
  const mockData = [
    {
      key: '1',
      orderSource: ['购买', '常规购买'],
      deviceScope: '单设备',
      orderNo: 'C20260228032002-13-OXOWU',
      status: '已支付',
      createTimeUTC: '2026-02-28 03:20:02',
      createTimeLocal: '2026-02-28 11:20:02',
      app: 'U家智联-Andriod-国内',
      appId: 'GFZUm2Tk18zPUFCT',
      appDirection: '设备',
      paymentMethod: '企业收款',
      appEnterprise: '微网优联科技（成都）有限公司',
      appEnterpriseId: 'QEBcW6rqyCMIOVUL',
      deviceEnterprise: '微网优联科技（成都）有限公司',
      deviceEnterpriseId: 'QEBcW6rqyCMIOVUL',
      productName: 'U-C201D-双目2MP4G云台机--国内版',
      productId: 'QEBw5aek7qkmiliq',
      isFirstSubscription: '是',
      userId: '1600010d40003cad',
      userAccount: '-',
      userRegTime: '-',
      license: 'QEBUHL26012700IA',
      region: '-',
      payChannel: '微信',
      payCurrency: '人民币',
      payAmount: '149',
      voucherCode: 'HM7F3K9A2B1C',
      voucherAmount: '¥ 149.00',
      payOrderNo: '4200003076202602280274956084',
      bizPayTimeUTC: '2026-02-28 03:20:18',
      bizPayTimeLocal: '2026-02-28 11:20:18',
      actualPayTimeUTC: '2026-02-28 03:20:18',
      actualPayTimeLocal: '2026-02-28 11:20:18',
      packageName: '7天循环录像【12个月】',
      packageId: 'WKfjwcCxUO',
      location: '国内',
      subscriptionType: '非订阅',
      duration: '12个月',
      isTrial: '是',
      trialStatus: '试用中',
      trialDays: 7,
      userFreeUse: '否',
      packageStatus: '生效中',
      packagePrice: '¥ 149.00',
      appleInAppKey: '-',
      googleInAppKey: '-',
      serviceType: '事件云存储',
      serviceName: '7天事件模板-12个月',
      serviceId: '12',
      serviceDuration: '12月',
      serviceFloorPrice: '50.0元, 10.0美元',
      packageServices: [
        { serviceType: '全天云存', serviceName: '全天云存-标清版', serviceId: 'SVC001', platformCost: '¥ 15.00，$ 2.20' },
        { serviceType: '鸟类识别', serviceName: 'AI鸟类智能识别', serviceId: 'SVC002', platformCost: '¥ 5.00，$ 0.20' }
      ],
      refundRecords: [],
      relatedOrders: [
        {
          key: 'r1',
          orderNo: 'REL-20260228-101',
          status: '已支付',
          payTimeUtc: '2026-02-28 03:25:00',
          payCurrency: '人民币',
          payAmount: '149.00',
          subscriptionType: '非订阅',
          createTimeUtc: '2026-02-28 03:20:00'
        },
        {
          key: 'r2',
          orderNo: 'REL-20260228-102',
          status: '已支付',
          payTimeUtc: '2026-02-28 04:10:00',
          payCurrency: '人民币',
          payAmount: '149.00',
          subscriptionType: '非订阅',
          createTimeUtc: '2026-02-28 04:05:00'
        }
      ]
    },
    {
      key: '2',
      orderSource: ['购买', '常规购买'],
      orderNo: 'C20260228045512-44-RE92K',
      status: '已全额退款',
      createTimeUTC: '2026-02-28 04:55:12',
      createTimeLocal: '2026-02-28 12:55:12',
      app: 'U家智联-iOS-国内',
      appId: 'AFZUm2Tk18zPUFXX',
      appDirection: '用户',
      paymentMethod: '企业收款',
      appEnterprise: '某电子科技有限公司',
      appEnterpriseId: 'QEBcW6rqyCMIO001',
      deviceEnterprise: '某电子科技有限公司',
      deviceEnterpriseId: 'QEBcW6rqyCMIO001',
      productName: '智能摄像机 Q1',
      productId: 'PROD_001',
      isFirstSubscription: '否',
      userId: '2700010d40003bbb',
      userAccount: 'test@example.com',
      userRegTime: '2025-01-01',
      license: 'LIC_882233',
      region: '华东',
      payChannel: '支付宝',
      payCurrency: '人民币',
      payAmount: '299',
      voucherCode: 'HM5T7R9E1W3Q',
      voucherAmount: '¥ 59.80',
      payOrderNo: '2026022822001476',
      bizPayTimeUTC: '2026-02-28 04:56:00',
      bizPayTimeLocal: '2026-02-28 12:56:00',
      actualPayTimeUTC: '2026-02-28 04:56:00',
      actualPayTimeLocal: '2026-02-28 12:56:00',
      packageName: '30天不间断录像【1个月】',
      packageId: 'PKG_30D_1M',
      location: '国内',
      subscriptionType: '订阅制',
      duration: '1个月',
      subscriptionPrice: '¥ 299.00',
      supportNonSub: '支持',
      nonSubPrice: '¥ 199.00',
      isTrial: '是',
      trialDays: 3,
      packageStatus: '已失效',
      appleInAppKey: '-',
      googleInAppKey: '-',
      serviceType: '全时云存储',
      serviceName: '30天全时模板-1个月',
      serviceId: '22',
      serviceDuration: '1月',
      serviceFloorPrice: '100.0元',
      packageServices: [
        { serviceType: '全时云存', serviceName: '全时云存-高清版', serviceId: 'SVC003', platformCost: '¥ 25.00，$ 3.50' }
      ],
      refundRecords: [
        {
          key: 'rf-2-1',
          refundMethod: '支付宝',
          refundCurrency: 'CNY',
          refundAmount: '299.00',
          refundTimeUTC: '2026-02-28 05:10:00',
          refundTimeLocal: '2026-02-28 13:10:00'
        }
      ],
      relatedOrders: [
        {
          key: 'r1',
          orderNo: 'REL-20260228-201',
          status: '已支付',
          payTimeUtc: '2026-02-28 04:56:00',
          payCurrency: '人民币',
          payAmount: '299.00',
          subscriptionType: '订阅制',
          createTimeUtc: '2026-02-28 04:55:00'
        }
      ]
    },
    {
      key: '3',
      orderSource: ['赠送'],
      deviceScope: '多设备',
      orderNo: 'C20260228061244-11-PLM90',
      status: '已部分退款',
      createTimeUTC: '2026-02-28 06:12:44',
      createTimeLocal: '2026-02-28 14:12:44',
      app: 'U家智联-Andriod-海外',
      appId: 'GFZUm2Tk18zPUFCT',
      appDirection: '设备',
      paymentMethod: '个人收款',
      appEnterprise: '智慧家居有限公司',
      appEnterpriseId: 'QEBcW6rqyCMIO999',
      deviceEnterprise: '智慧家居有限公司',
      deviceEnterpriseId: 'QEBcW6rqyCMIO999',
      productName: '户外防水机 W2',
      productId: 'PROD_W2',
      isFirstSubscription: '是',
      userId: '3800010d40003ccc',
      userAccount: '13800000000',
      userRegTime: '2025-10-10',
      license: 'LIC_998877',
      region: '华南',
      payChannel: 'Paypal',
      payCurrency: '美元',
      payAmount: '29.9',
      payOrderNo: 'PP-202602289988',
      bizPayTimeUTC: '2026-02-28 06:15:00',
      bizPayTimeLocal: '2026-02-28 14:15:00',
      actualPayTimeUTC: '2026-02-28 06:15:00',
      actualPayTimeLocal: '2026-02-28 14:15:00',
      packageName: '7天循环录像【6个月】',
      packageId: 'PKG_7D_6M',
      location: '海外',
      subscriptionType: '非订阅',
      duration: '6个月',
      isTrial: '否',
      trialStatus: '试用结束',
      packageStatus: '待生效',
      packagePrice: '$ 29.90',
      appleInAppKey: '-',
      googleInAppKey: '-',
      serviceType: '事件云存储',
      serviceName: '7天事件模板-6个月',
      serviceId: '13',
      serviceDuration: '6月',
      serviceFloorPrice: '60.0元',
      packageServices: [
        { serviceType: '事件云存', serviceName: '事件云存-标清版', serviceId: 'SVC004', platformCost: '¥ 10.00，$ 1.50' }
      ],
      refundRecords: [
        {
          key: 'rf-3-1',
          refundMethod: 'Paypal',
          refundCurrency: 'USD',
          refundAmount: '5.00',
          refundTimeUTC: '2026-02-28 07:00:00',
          refundTimeLocal: '2026-02-28 15:00:00'
        },
        {
          key: 'rf-3-2',
          refundMethod: 'Paypal',
          refundCurrency: 'USD',
          refundAmount: '10.00',
          refundTimeUTC: '2026-02-28 09:30:00',
          refundTimeLocal: '2026-02-28 17:30:00'
        }
      ],
      relatedOrders: [
        {
          key: 'r1',
          orderNo: 'REL-20260228-301',
          status: '已支付',
          payTimeUtc: '2026-02-28 06:20:00',
          payCurrency: '美元',
          payAmount: '29.99',
          subscriptionType: '非订阅',
          createTimeUtc: '2026-02-28 06:15:00'
        },
        {
          key: 'r2',
          orderNo: 'REL-20260228-302',
          status: '已全额退款',
          payTimeUtc: '2026-02-28 07:05:00',
          payCurrency: '美元',
          payAmount: '10.00',
          subscriptionType: '非订阅',
          createTimeUtc: '2026-02-28 07:00:00'
        },
        {
          key: 'r3',
          orderNo: 'REL-20260228-303',
          status: '已部分退款',
          payTimeUtc: '2026-02-28 08:30:00',
          payCurrency: '美元',
          payAmount: '15.50',
          subscriptionType: '非订阅',
          createTimeUtc: '2026-02-28 08:25:00'
        }
      ]
    },
    {
      key: '4',
      orderSource: ['购买', '常规购买'],
      deviceScope: '单设备',
      orderNo: 'C20260228080000-99-QAZWS',
      status: '申请退款中',
      createTimeUTC: '2026-02-28 08:00:00',
      createTimeLocal: '2026-02-28 16:00:00',
      app: 'U家智联-iOS-全球通用',
      appId: 'GFZUm2Tk18zPUFCT',
      appDirection: '设备',
      paymentMethod: '企业收款',
      appEnterprise: '微网优联科技',
      appEnterpriseId: 'QEBcW6rqyCMIOVUL',
      deviceEnterprise: '微网优联科技',
      deviceEnterpriseId: 'QEBcW6rqyCMIOVUL',
      productName: '双目室外机',
      productId: 'QEBw5aek7q',
      isFirstSubscription: '是',
      userId: '4900010d40003ddd',
      userAccount: '-',
      userRegTime: '-',
      license: 'LIC_112233',
      region: '西北',
      payChannel: 'ApplePay',
      payCurrency: '美元',
      payAmount: '12.99',
      voucherCode: 'HM1A3S5D7F9G',
      voucherAmount: '$ 3.00',
      payOrderNo: 'AP-202602281122',
      bizPayTimeUTC: '2026-02-28 08:05:00',
      bizPayTimeLocal: '2026-02-28 16:05:00',
      actualPayTimeUTC: '2026-02-28 08:05:00',
      actualPayTimeLocal: '2026-02-28 16:05:00',
      packageName: '3天循环录像【1个月】',
      packageId: 'PKG_3D_1M',
      location: '全球',
      subscriptionType: '订阅制',
      duration: '1个月',
      subscriptionPrice: '¥ 88.00，$ 10.00',
      supportNonSub: '不支持',
      packageStatus: '生效中',
      appleInAppKey: 'apple.sub.7d.1m',
      googleInAppKey: 'google.sub.7d.1m',
      serviceType: '事件云存储',
      serviceName: '3天事件模板-1个月',
      serviceId: '11',
      serviceDuration: '1月',
      serviceFloorPrice: '20.0元',
      packageServices: [
        { serviceType: '事件云存', serviceName: '事件云存-标清版', serviceId: 'SVC005', platformCost: '¥ 8.00，$ 1.20' },
        { serviceType: '人脸识别', serviceName: 'AI人脸识别', serviceId: 'SVC006', platformCost: '¥ 3.00，$ 0.50' }
      ],
      refundRecords: [],
      relatedOrders: [
        {
          key: 'r1',
          orderNo: 'REL-20260228-401',
          status: '已支付',
          payTimeUtc: '2026-02-28 08:10:00',
          payCurrency: '美元',
          payAmount: '12.99',
          subscriptionType: '订阅制',
          createTimeUtc: '2026-02-28 08:05:00'
        },
        {
          key: 'r2',
          orderNo: 'REL-20260228-402',
          status: '申请退款中',
          payTimeUtc: '2026-02-28 09:00:00',
          payCurrency: '美元',
          payAmount: '12.99',
          subscriptionType: '订阅制',
          createTimeUtc: '2026-02-28 08:55:00'
        }
      ]
    },
    {
      key: '5',
      orderSource: ['购买', '活动专享', '单次解锁事件记录'],
      deviceScope: '单设备',
      orderNo: 'C20260228120000-66-UNL01',
      status: '已支付',
      createTimeUTC: '2026-02-28 12:00:00',
      createTimeLocal: '2026-02-28 20:00:00',
      app: 'U家智联-Andriod-国内',
      appId: 'GFZUm2Tk18zPUFCT',
      appDirection: '设备',
      paymentMethod: '企业收款',
      appEnterprise: '微网优联科技（成都）有限公司',
      appEnterpriseId: 'QEBcW6rqyCMIOVUL',
      deviceEnterprise: '微网优联科技（成都）有限公司',
      deviceEnterpriseId: 'QEBcW6rqyCMIOVUL',
      productName: 'U-C201D-双目2MP4G云台机--国内版',
      productId: 'QEBw5aek7qkmiliq',
      isFirstSubscription: '-',
      userId: '1600010d40003cad',
      userAccount: '-',
      userRegTime: '-',
      license: 'QEBUHL26012700IA',
      region: '-',
      payChannel: '微信',
      payCurrency: '人民币',
      payAmount: '9.9',
      payOrderNo: '4200003076202602280275000001',
      bizPayTimeUTC: '2026-02-28 12:00:18',
      bizPayTimeLocal: '2026-02-28 20:00:18',
      actualPayTimeUTC: '2026-02-28 12:00:18',
      actualPayTimeLocal: '2026-02-28 20:00:18',
      packageName: '-',
      packageId: '-',
      location: '国内',
      subscriptionType: '-',
      duration: '-',
      isTrial: '-',
      trialStatus: '-',
      trialDays: '-',
      userFreeUse: '-',
      packageStatus: '-',
      packagePrice: '-',
      appleInAppKey: '-',
      googleInAppKey: '-',
      serviceType: '-',
      serviceName: '-',
      serviceId: '-',
      serviceDuration: '-',
      serviceFloorPrice: '-',
      packageServices: [],
      refundRecords: [],
      relatedOrders: []
    }
  ];

  // 状态颜色映射函数
  const getStatusColor = (status) => {
    switch (status) {
      case '已支付': return 'green';
      case '已全额退款': return 'default';
      case '已部分退款': return 'orange';
      case '申请退款中': return 'red';
      case '已作废': return 'default';
      default: return 'blue';
    }
  };

  // 所有可选列配置
  const allColumns = [
    { title: '订单编号', dataIndex: 'orderNo', key: 'orderNo', width: 220, fixed: 'left' },
    { 
      title: '订单状态', 
      dataIndex: 'status', 
      key: 'status',
      width: 120,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>
    },
    {
      title: '订单来源',
      dataIndex: 'orderSource',
      key: 'orderSource',
      width: 150,
      render: (orderSource) => renderTagLines(getOrderSourceLabels(orderSource), ORDER_SOURCE_COLORS)
    },
    {
      title: '应用方向',
      key: 'appDirection',
      width: 110,
      render: (_, record) => renderTagLines(getAppDirectionLabels(record), APP_DIRECTION_COLORS)
    },
    { 
      title: '应用信息', 
      key: 'appInfo',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.app}</div>
        </div>
      )
    },
    { 
      title: '订阅类型', 
      dataIndex: 'subscriptionType', 
      key: 'subscriptionType', 
      width: 100,
      render: (val) => val === '订阅制' ? <Tag color="blue">{val}</Tag> : <Tag>{val}</Tag>
    },
    { 
      title: '创建时间', 
      key: 'createTimeInfo',
      width: 180,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.createTimeUTC} (UTC)</div>
          <div style={{ color: '#999' }}>{record.createTimeLocal} (Local)</div>
        </div>
      )
    },
    { 
      title: '支付时间', 
      key: 'payTimeInfo',
      width: 180,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.actualPayTimeUTC} (UTC)</div>
          <div style={{ color: '#999' }}>{record.actualPayTimeLocal} (Local)</div>
        </div>
      )
    },
    { 
      title: '套餐信息', 
      key: 'packageInfo',
      width: 200,
      render: (_, record) => isUnlockOrder(record) ? <span style={{ color: '#ccc' }}>-</span> : (
        <div style={{ fontSize: '12px' }}>
          <div>{record.packageName}</div>
          <div style={{ color: '#999' }}>ID: {record.packageId}</div>
        </div>
      )
    },
    { 
        title: '产品信息', 
        key: 'productInfo',
        width: 200,
        render: (_, record) => (
          <div style={{ fontSize: '12px' }}>
            <div>{record.productName}</div>
            <div style={{ color: '#999' }}>ID: {record.productId}</div>
          </div>
        )
    },
    { title: '支付通道', dataIndex: 'payChannel', key: 'payChannel', width: 100 },
    { title: '支付币种', dataIndex: 'payCurrency', key: 'payCurrency', width: 100 },
    { title: '支付金额', dataIndex: 'payAmount', key: 'payAmount', width: 100, render: (val) => `¥${val}` },
    {
      title: '优惠券码',
      dataIndex: 'voucherCode',
      key: 'voucherCode',
      width: 160,
      render: (val) => val || <span style={{ color: '#ccc' }}>-</span>,
    },
    {
      title: '优惠金额',
      dataIndex: 'voucherAmount',
      key: 'voucherAmount',
      width: 110,
      render: (val) => (val ? <span style={{ color: '#cf1322' }}>{val}</span> : <span style={{ color: '#ccc' }}>-</span>),
    },
    {
      title: '最后退款',
      key: 'lastRefund',
      width: 170,
      render: (_, record) => {
        const lastRefundTime = getLastRefundTime(record);
        return lastRefundTime
          ? <div style={{ fontSize: '12px' }}>{lastRefundTime} (Local)</div>
          : <span style={{ color: '#ccc' }}>-</span>;
      }
    },
    {
      title: '退款总额',
      key: 'refundTotal',
      width: 120,
      render: (_, record) => {
        const total = getRefundTotal(record);
        return total > 0
          ? `${getCurrencySymbol(record.payCurrency)}${total.toFixed(2)}`
          : <span style={{ color: '#ccc' }}>-</span>;
      }
    },
    { 
        title: '设备企业', 
        key: 'deviceEnterprise',
        width: 220,
        render: (_, record) => (
          <div style={{ fontSize: '12px' }}>
            <div>{record.deviceEnterprise}</div>
            <div style={{ color: '#999' }}>ID: {record.deviceEnterpriseId}</div>
          </div>
        )
    },
    {
        title: 'App企业', 
        key: 'appEnterprise',
        width: 220,
        render: (_, record) => (
          <div style={{ fontSize: '12px' }}>
            <div>{record.appEnterprise}</div>
            <div style={{ color: '#999' }}>ID: {record.appEnterpriseId}</div>
          </div>
        )
    },
    { title: '用户ID', dataIndex: 'userId', key: 'userId', width: 180 },
  ];

  // 默认展示的列 Key
  const defaultKeys = ['orderNo', 'status', 'orderSource', 'appDirection', 'appInfo', 'subscriptionType', 'createTimeInfo', 'payTimeInfo', 'packageInfo', 'payChannel', 'payCurrency', 'payAmount', 'voucherCode', 'voucherAmount', 'lastRefund', 'refundTotal'];
  const [targetKeys, setTargetKeys] = useState(defaultKeys);

  // 根据选择过滤列
  const filteredColumns = [
    ...allColumns.filter(col => targetKeys.includes(col.key)),
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 180,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="text" 
            size="small"
            style={{ color: '#1890ff', display: 'flex', alignItems: 'center' }}
            icon={<EyeOutlined />} 
            onClick={() => showDetail(record)}
          >
            详情
          </Button>
          {!isUnlockOrder(record) && (
            <Button 
              type="text" 
              size="small"
              danger 
              style={{ display: 'flex', alignItems: 'center' }}
              icon={<RollbackOutlined />} 
              onClick={() => {
                setCurrentOrder(record);
                setRefundVisible(true);
                setRefundType('full');
              }}
            >
              退款
            </Button>
          )}
        </Space>
      ),
    }
  ];

  const showDetail = (record) => {
    setCurrentOrder(record);
    setDetailVisible(true);
  };

  const onReset = () => {
    form.resetFields();
  };

  // 导出增值服务订单
  const handleExport = () => {
    if (mockData.length === 0) {
      message.warning('没有可导出的数据');
      return;
    }
    // TODO: 联调时替换为接口导出
    const headers = [
      '订单编号', '订单状态', '订单来源', '应用方向', '应用信息', '订阅类型',
      '创建时间(UTC)', '创建时间(Local)', '支付时间(UTC)', '支付时间(Local)',
      '套餐名称', '支付通道', '支付币种', '支付金额', '优惠券码', '优惠金额', '最后退款', '退款总额'
    ];
    const rows = mockData.map((o) => {
      const lastRefundTime = getLastRefundTime(o) || '-';
      const refundTotal = getRefundTotal(o);
      const refundTotalText = refundTotal > 0 ? `${getCurrencySymbol(o.payCurrency)}${refundTotal.toFixed(2)}` : '-';
      return [
        o.orderNo,
        o.status,
        getOrderSourceLabels(o.orderSource).join('/'),
        getAppDirectionLabels(o).join('/'),
        o.app,
        o.subscriptionType,
        o.createTimeUTC,
        o.createTimeLocal,
        o.actualPayTimeUTC,
        o.actualPayTimeLocal,
        o.packageName,
        o.payChannel,
        o.payCurrency,
        o.payAmount,
        o.voucherCode || '-',
        o.voucherAmount || '-',
        lastRefundTime,
        refundTotalText
      ].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `增值服务订单导出_${new Date().toLocaleDateString()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.success('导出成功');
  };

  const getFields = () => {
    const fields = [
      // 第一行
      <Col span={6} key="orderNo"><Form.Item name="orderNo" label="订单编号" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}><Input placeholder="请输入订单编号" /></Form.Item></Col>,
      <Col span={6} key="status">
        <Form.Item name="status" label="订单状态" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
          <Select placeholder="订单状态">
            <Option value="paid">已支付</Option>
            <Option value="refunded">已全额退款</Option>
            <Option value="partial_refunded">已部分退款</Option>
            <Option value="refunding">申请退款中</Option>
            <Option value="voided">已作废</Option>
          </Select>
        </Form.Item>
      </Col>,
      <Col span={6} key="license"><Form.Item name="license" label="License" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}><Input placeholder="请输入License" /></Form.Item></Col>,
      <Col span={6} key="payChannel">
        <Form.Item name="payChannel" label="支付通道" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
          <Select placeholder="支付通道">
            <Option value="alipay">支付宝</Option>
            <Option value="wechat">微信</Option>
            <Option value="paypal">Paypal</Option>
            <Option value="applepay">ApplePay</Option>
            <Option value="googlepay">GooglePay</Option>
            <Option value="payssion">PAYSSION</Option>
            <Option value="zarinpal">ZARINPAL</Option>
            <Option value="airwallex">AIRWALLEX</Option>
          </Select>
        </Form.Item>
      </Col>,

      // 第二行
      <Col span={6} key="payCurrency">
        <Form.Item name="payCurrency" label="支付币种" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
          <Select placeholder="支付币种">
            <Option value="CNY">人民币</Option>
            <Option value="USD">美元</Option>
          </Select>
        </Form.Item>
      </Col>,
      <Col span={6} key="payAmount">
        <Form.Item name="payAmount" label="支付金额" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
          <Input.Group compact style={{ display: 'flex', gap: '4px' }}>
            <Form.Item name="payAmountMin" noStyle>
              <InputNumber placeholder="支付金额" style={{ flex: 1 }} />
            </Form.Item>
            <span style={{ lineHeight: '32px' }}>-</span>
            <Form.Item name="payAmountMax" noStyle>
              <InputNumber placeholder="支付金额" style={{ flex: 1 }} />
            </Form.Item>
          </Input.Group>
        </Form.Item>
      </Col>,
      <Col span={6} key="payOrderNo"><Form.Item name="payOrderNo" label="支付单号" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}><Input placeholder="请输入支付单号" /></Form.Item></Col>,
      <Col span={6} key="voucherCode"><Form.Item name="voucherCode" label="优惠券码" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}><Input placeholder="请输入优惠券码" /></Form.Item></Col>,
      <Col span={6} key="payTime">
        <Form.Item name="payTime" label="支付时间" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
          <RangePicker style={{ width: '100%' }} placeholder={['Local支付时间开始', 'Local支付时间结束']} />
        </Form.Item>
      </Col>,
      <Col span={6} key="lastRefund">
        <Form.Item name="lastRefund" label="最后退款" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
          <RangePicker style={{ width: '100%' }} placeholder={['Local最后退款开始', 'Local最后退款结束']} />
        </Form.Item>
      </Col>,
      <Col span={6} key="orderSource">
        <Form.Item name="orderSource" label="订单来源" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
          <Cascader options={ORDER_SOURCE_OPTIONS} placeholder="订单来源" allowClear />
        </Form.Item>
      </Col>,
      <Col span={6} key="appDirection">
        <Form.Item name="appDirection" label="应用方向" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
          <Cascader options={APP_DIRECTION_OPTIONS} placeholder="应用方向" allowClear />
        </Form.Item>
      </Col>,
    ];

    if (expand) {
      fields.push(
        // 第三行 - 可切换标题查询
        <Col span={6} key="appSearch">
          <Form.Item label="" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <Form.Item name="appSearchKey" noStyle initialValue="appName">
                <Select style={{ width: '100px' }}>
                  <Option value="appName">下单应用</Option>
                  <Option value="appId">AppID</Option>
                </Select>
              </Form.Item>
              <Form.Item name="appSearchValue" noStyle>
                <Input style={{ flex: 1 }} placeholder="请输入" />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        </Col>,
        <Col span={6} key="appEnterpriseSearch">
          <Form.Item label="" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <Form.Item name="appEntKey" noStyle initialValue="entName">
                <Select style={{ width: '100px' }}>
                  <Option value="entName">App企业</Option>
                  <Option value="entId">企业ID</Option>
                </Select>
              </Form.Item>
              <Form.Item name="appEntValue" noStyle>
                <Input style={{ flex: 1 }} placeholder="请输入" />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        </Col>,
        <Col span={6} key="deviceEnterpriseSearch">
          <Form.Item label="" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <Form.Item name="devEntKey" noStyle initialValue="entName">
                <Select style={{ width: '100px' }}>
                  <Option value="entName">设备企业</Option>
                  <Option value="entId">企业ID</Option>
                </Select>
              </Form.Item>
              <Form.Item name="devEntValue" noStyle>
                <Input style={{ flex: 1 }} placeholder="请输入" />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        </Col>,
        <Col span={6} key="packageSearch">
          <Form.Item label="" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
            <Input.Group compact style={{ display: 'flex' }}>
              <Form.Item name="pkgKey" noStyle initialValue="pkgName">
                <Select style={{ width: '100px' }}>
                  <Option value="pkgName">套餐名称</Option>
                  <Option value="pkgId">套餐ID</Option>
                </Select>
              </Form.Item>
              <Form.Item name="pkgValue" noStyle>
                <Input style={{ flex: 1 }} placeholder="请输入" />
              </Form.Item>
            </Input.Group>
          </Form.Item>
        </Col>,

        // 第四行
        <Col span={6} key="userId"><Form.Item name="userId" label="用户ID" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}><Input placeholder="请输入用户ID" /></Form.Item></Col>
      );
    }

    return fields;
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>增值服务订单管理</Title>
      </div>
      {/* 搜索区域 */}
      <Card style={{ marginBottom: '16px' }}>
        <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
          <Row gutter={[24, 16]}>
            {getFields()}
            <Col span={expand ? 12 : 24} style={{ textAlign: 'right', marginLeft: 'auto' }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />}>查询</Button>
                <Button icon={<ReloadOutlined />} onClick={onReset}>重置</Button>
                <Button type="link" onClick={() => setExpand(!expand)}>
                  {expand ? <UpOutlined /> : <DownOutlined />} {expand ? '收起' : '展开'}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 统计及操作区域 */}
      <Card style={{ marginBottom: '16px' }}>
        <Row align="middle" justify="space-between">
          <Col flex="auto">
            {/* 订单支付 */}
            <Row align="middle" style={{ marginBottom: 12 }}>
              <Col style={{ width: 80 }}>
                <Text strong style={{ fontSize: 14 }}>订单支付</Text>
              </Col>
              <Col style={{ width: 80 }}>
                <Text type="secondary">笔数：</Text>
                <Text strong style={{ fontSize: 15 }}>{paymentStats.count}</Text>
              </Col>
              <Col style={{ width: 200 }}>
                <Text type="secondary">本页小计：</Text>
                <Text strong style={{ fontSize: 15, color: '#cf1322' }}>¥ {paymentStats.pageSubtotal.rmb}</Text>
                <Text strong style={{ fontSize: 15, color: '#cf1322', marginLeft: 8 }}>$ {paymentStats.pageSubtotal.usd}</Text>
              </Col>
              <Col>
                <Text type="secondary">总计：</Text>
                <Text strong style={{ fontSize: 15 }}>¥ {paymentStats.total.rmb}</Text>
                <Text strong style={{ fontSize: 15, marginLeft: 8 }}>$ {paymentStats.total.usd}</Text>
              </Col>
            </Row>
            {/* 订单退款 */}
            <Row align="middle">
              <Col style={{ width: 80 }}>
                <Text strong style={{ fontSize: 14 }}>订单退款</Text>
              </Col>
              <Col style={{ width: 80 }}>
                <Text type="secondary">笔数：</Text>
                <Text strong style={{ fontSize: 15 }}>{refundStats.count}</Text>
              </Col>
              <Col style={{ width: 200 }}>
                <Text type="secondary">本页小计：</Text>
                <Text strong style={{ fontSize: 15, color: '#08979c' }}>¥ {refundStats.pageSubtotal.rmb}</Text>
                <Text strong style={{ fontSize: 15, color: '#08979c', marginLeft: 8 }}>$ {refundStats.pageSubtotal.usd}</Text>
              </Col>
              <Col>
                <Text type="secondary">总计：</Text>
                <Text strong style={{ fontSize: 15 }}>¥ {refundStats.total.rmb}</Text>
                <Text strong style={{ fontSize: 15, marginLeft: 8 }}>$ {refundStats.total.usd}</Text>
              </Col>
            </Row>
          </Col>
          <Col>
            <Space>
              <Button icon={<ExportOutlined />} onClick={handleExport}>导出数据</Button>
              <Button icon={<SettingOutlined />} onClick={() => setColumnConfigVisible(true)}>列表配置</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table 
          columns={filteredColumns} 
          dataSource={mockData} 
          scroll={{ x: 1850 }}
          pagination={{ 
            pageSize: 10,
            showTotal: (total) => `共 ${total} 条数据`
          }}
        />
      </Card>

      {/* 字段选择 Modal */}
      <Modal
        title="自定义列表字段"
        open={columnConfigVisible}
        onOk={() => {
          setColumnConfigVisible(false);
        }}
        onCancel={() => setColumnConfigVisible(false)}
        width={600}
        okText="确定"
        cancelText="取消"
      >
        <Transfer
          dataSource={allColumns.map(item => ({ key: item.key, title: item.title }))}
          targetKeys={targetKeys}
          onChange={(newTargetKeys) => setTargetKeys(newTargetKeys)}
          render={item => item.title}
          listStyle={{ width: 250, height: 400 }}
          titles={['未选字段', '已选字段']}
          operations={['添加到列表', '移除']}
          locale={{ 
            itemUnit: '项', 
            itemsUnit: '项', 
            searchPlaceholder: '请输入搜索内容',
            selectAll: '选择全部',
            selectCurrent: '选择当前页',
            selectInvert: '反选当前页',
            removeAll: '移除全部',
            removeCurrent: '移除当前页'
          }}
        />
      </Modal>

      {/* 订单详情 Modal */}
      <Modal
        title="订单详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={1000}
        centered
      >
        {currentOrder && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px', paddingTop: '16px' }}>
            <Descriptions title="基础信息" bordered column={2} size="small">
              <Descriptions.Item label="订单编号">{currentOrder.orderNo}</Descriptions.Item>
              <Descriptions.Item label="订单状态"><Tag color={getStatusColor(currentOrder.status)}>{currentOrder.status}</Tag></Descriptions.Item>
              <Descriptions.Item label="创建时间(UTC)">{currentOrder.createTimeUTC}</Descriptions.Item>
              <Descriptions.Item label="创建时间(Local)">{currentOrder.createTimeLocal}</Descriptions.Item>
              <Descriptions.Item label="下单应用">{currentOrder.app}</Descriptions.Item>
              <Descriptions.Item label="APPID">{currentOrder.appId}</Descriptions.Item>
              <Descriptions.Item label="App企业">{currentOrder.appEnterprise}</Descriptions.Item>
              <Descriptions.Item label="App企业ID">{currentOrder.appEnterpriseId}</Descriptions.Item>
              <Descriptions.Item label="收款方式">{currentOrder.paymentMethod}</Descriptions.Item>
              <Descriptions.Item label="订单来源">{renderTagLines(getOrderSourceLabels(currentOrder.orderSource), ORDER_SOURCE_COLORS)}</Descriptions.Item>
            </Descriptions>

            <Divider />
            
            <Descriptions title="套餐信息" bordered column={2} size="small">
              <Descriptions.Item label="套餐名称">{currentOrder.packageName}</Descriptions.Item>
              <Descriptions.Item label="套餐ID">{currentOrder.packageId}</Descriptions.Item>
              <Descriptions.Item label="应用方向">{renderTagLines(getAppDirectionLabels(currentOrder), APP_DIRECTION_COLORS)}</Descriptions.Item>
              <Descriptions.Item label="生效时长">{currentOrder.duration}</Descriptions.Item>
              <Descriptions.Item label="上架区域">{currentOrder.location || '国内'}</Descriptions.Item>
              <Descriptions.Item label="订阅类型">{currentOrder.subscriptionType}</Descriptions.Item>

              {currentOrder.subscriptionType === '订阅制' ? (
                <>
                  <Descriptions.Item label="订阅售价" span={2}>{currentOrder.subscriptionPrice}</Descriptions.Item>
                  <Descriptions.Item label="免费试用">
                    {currentOrder.isTrial === '是' ? `是（${currentOrder.trialDays}天）` : '否'}
                  </Descriptions.Item>
                  {currentOrder.supportNonSub === '支持' && (
                    <Descriptions.Item label="不订阅售价">{currentOrder.nonSubPrice}</Descriptions.Item>
                  )}
                </>
              ) : (
                <>
                  <Descriptions.Item label="售价">{currentOrder.packagePrice}</Descriptions.Item>
                  <Descriptions.Item label="用户免费使用">{currentOrder.userFreeUse || '否'}</Descriptions.Item>
                </>
              )}

              <Descriptions.Item label="Apple 内购key">{currentOrder.appleInAppKey || '-'}</Descriptions.Item>
              <Descriptions.Item label="Google 内购key">{currentOrder.googleInAppKey || '-'}</Descriptions.Item>
              <Descriptions.Item label="套餐状态" span={2}>
                <Tag color={currentOrder.packageStatus === '生效中' ? 'success' : currentOrder.packageStatus === '待生效' ? 'processing' : 'default'}>
                  {currentOrder.packageStatus}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <div style={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '16px', color: 'rgba(0, 0, 0, 0.85)' }}>套餐服务</div>
            <Table
              size="small"
              bordered
              pagination={false}
              columns={[
                { title: '套餐服务ID', dataIndex: 'serviceId', key: 'serviceId', width: 120 },
                { title: '套餐服务', dataIndex: 'serviceName', key: 'serviceName', width: 200 },
                { title: '服务类型', dataIndex: 'serviceType', key: 'serviceType', width: 120 },
                { title: '平台成本', dataIndex: 'platformCost', key: 'platformCost', width: 150 }
              ]}
              dataSource={(currentOrder.packageServices || [
                {
                  key: '1',
                  serviceId: 'SVC001',
                  serviceName: '全天云存-标清版',
                  serviceType: '全天云存',
                  platformCost: '¥ 15.00，$ 2.20'
                },
                {
                  key: '2',
                  serviceId: 'SVC002',
                  serviceName: 'AI鸟类智能识别',
                  serviceType: '鸟类识别',
                  platformCost: '¥ 5.00，$ 0.20'
                }
              ]).map((service, index) => ({ ...service, key: service.key || index }))}
            />

            <Divider />

            <Descriptions title="设备&用户信息" bordered column={2} size="small">
              {currentOrder.appDirection === '设备' ? (
                <>
                  <Descriptions.Item label="产品名称">{currentOrder.productName}</Descriptions.Item>
                  <Descriptions.Item label="产品ID">{currentOrder.productId}</Descriptions.Item>
                  <Descriptions.Item label="设备企业">{currentOrder.deviceEnterprise}</Descriptions.Item>
                  <Descriptions.Item label="设备企业ID">{currentOrder.deviceEnterpriseId}</Descriptions.Item>
                  <Descriptions.Item label="License">{currentOrder.license}</Descriptions.Item>
                </>
              ) : (
                <>
                  <Descriptions.Item label="用户账号">{currentOrder.userAccount}</Descriptions.Item>
                  <Descriptions.Item label="用户ID">{currentOrder.userId}</Descriptions.Item>
                </>
              )}
            </Descriptions>

            <Divider />

            <Descriptions title="支付信息" bordered column={2} size="small">
              <Descriptions.Item label="支付通道">{currentOrder.payChannel}</Descriptions.Item>
              <Descriptions.Item label="支付币种">{currentOrder.payCurrency}</Descriptions.Item>
              <Descriptions.Item label="支付金额">{currentOrder.payAmount}</Descriptions.Item>
              <Descriptions.Item label="优惠券码">{currentOrder.voucherCode || '-'}</Descriptions.Item>
              <Descriptions.Item label="优惠金额">{currentOrder.voucherAmount || '-'}</Descriptions.Item>
              <Descriptions.Item label="支付单号">{currentOrder.payOrderNo}</Descriptions.Item>
              <Descriptions.Item label="业务支付时间(UTC)">{currentOrder.bizPayTimeUTC}</Descriptions.Item>
              <Descriptions.Item label="业务支付时间(Local)">{currentOrder.bizPayTimeLocal}</Descriptions.Item>
              <Descriptions.Item label="实际支付时间(UTC)">{currentOrder.actualPayTimeUTC}</Descriptions.Item>
              <Descriptions.Item label="实际支付时间(Local)">{currentOrder.actualPayTimeLocal}</Descriptions.Item>
            </Descriptions>

            <Divider />

            <div style={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '16px', color: 'rgba(0, 0, 0, 0.85)' }}>退款记录</div>
            <Table
              size="small"
              bordered
              pagination={false}
              columns={[
                { title: '退款方式', dataIndex: 'refundMethod', key: 'refundMethod', width: 100 },
                { title: '退款币种', dataIndex: 'refundCurrency', key: 'refundCurrency', width: 100 },
                { title: '退款金额', dataIndex: 'refundAmount', key: 'refundAmount', width: 120, render: (val, record) => `${getCurrencySymbol(record.refundCurrency)}${val}` },
                { title: '退款时间(UTC)', dataIndex: 'refundTimeUTC', key: 'refundTimeUTC' },
                { title: '退款时间(Local)', dataIndex: 'refundTimeLocal', key: 'refundTimeLocal' },
              ]}
              dataSource={(currentOrder.refundRecords || []).map((r, index) => ({ ...r, key: r.key || index }))}
              summary={(records) => {
                const total = records.reduce((sum, r) => sum + (parseFloat(r.refundAmount) || 0), 0);
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}><Text strong>退款总额</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <Text strong>{total > 0 ? `${getCurrencySymbol(currentOrder.payCurrency)}${total.toFixed(2)}` : '-'}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} colSpan={2} />
                  </Table.Summary.Row>
                );
              }}
              locale={{ emptyText: '暂无退款记录' }}
            />

            <Divider />

            <div style={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '16px', color: 'rgba(0, 0, 0, 0.85)' }}>关联订单组</div>
            <Table
              size="small"
              bordered
              pagination={false}
              columns={[
                { title: '订单编号', dataIndex: 'orderNo', key: 'orderNo' },
                { 
                  title: '订单状态', 
                  dataIndex: 'status', 
                  key: 'status',
                  render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>
                },
                { title: '支付时间(UTC)', dataIndex: 'payTimeUtc', key: 'payTimeUtc' },
                { title: '支付币种', dataIndex: 'payCurrency', key: 'payCurrency' },
                { title: '支付金额', dataIndex: 'payAmount', key: 'payAmount' },
                { 
                  title: '订阅类型', 
                  dataIndex: 'subscriptionType', 
                  key: 'subscriptionType',
                  render: (val) => val === '订阅制' ? <Tag color="blue">{val}</Tag> : <Tag>{val}</Tag>
                },
                { title: '创建时间(UTC)', dataIndex: 'createTimeUtc', key: 'createTimeUtc' },
                {
                  title: '操作',
                  key: 'action',
                  render: () => <Button type="link" size="small">查看详情</Button>
                }
              ]}
              dataSource={currentOrder.relatedOrders || []}
              locale={{ emptyText: '暂无关联订单记录' }}
            />
          </div>
        )}
      </Modal>

      {/* 订单退款 Modal */}
      <Modal
        title="订单退款"
        open={refundVisible}
        onCancel={() => setRefundVisible(false)}
        onOk={() => {
          message.success('已退款完成');
          setRefundVisible(false);
        }}
        okText="确认订单退款"
        cancelText="取消"
        width={600}
        centered
      >
        {currentOrder && (
          <div style={{ padding: '12px 0' }}>
            <Descriptions bordered column={1} size="small" labelStyle={{ width: '120px', background: '#fafafa' }}>
              <Descriptions.Item label="所属应用">{currentOrder.app}</Descriptions.Item>
              <Descriptions.Item label="AppID">{currentOrder.appId}</Descriptions.Item>
              <Descriptions.Item label="App币种">{currentOrder.payCurrency}</Descriptions.Item>
              <Descriptions.Item label="支付金额">
                <span style={{ color: '#f5222d', fontWeight: 'bold' }}>{currentOrder.payAmount}</span>
              </Descriptions.Item>
              <Descriptions.Item label="服务时长(天)">{currentOrder.serviceDuration || '31'}</Descriptions.Item>
              <Descriptions.Item label="已使用(天)">0</Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: '24px', border: '1px solid #f0f0f0', padding: '16px', borderRadius: '4px', background: '#fff' }}>
              <Form layout="vertical">
                <Form.Item label="退款金额额记录">
                  <Radio.Group onChange={(e) => setRefundType(e.target.value)} value={refundType}>
                    <Space direction="vertical">
                      <Radio value="full">全额退款</Radio>
                      <Radio value="custom">
                        自定义金额
                        {refundType === 'custom' && (
                          <Input 
                            size="small" 
                            style={{ width: 120, marginLeft: 10 }} 
                            placeholder="请输入金额" 
                            suffix={currentOrder.payCurrency} 
                          />
                        )}
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                
                <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', padding: '8px 12px', borderRadius: '4px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '13px', color: 'rgba(0,0,0,0.65)' }}>
                    系统建议：{currentOrder.payAmount} - {currentOrder.payAmount} * 0 / 31 = <span style={{ color: '#f5222d', fontWeight: 'bold' }}>{currentOrder.payAmount} {currentOrder.payCurrency}</span>
                  </span>
                </div>

                <div style={{ fontSize: '13px', color: '#8c8c8c', lineHeight: '1.6' }}>
                  退款提醒：请联系财务确认此订单在支付平台的退款是否完成！确认退款后，订单状态更新为已退款，若涉及平台对企业的分润，企业将退回分润到平台！
                </div>
              </Form>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ValueAddedOrder;
